import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Music2, Mic, Sparkles, Plus, Trash2, Save, Loader2, Download,
  FolderOpen, Play, Pause, Volume2, ArrowDownToLine, Search, X, Upload, Square, Wand2, Package
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { appConfirm } from "@/components/AppDialogHost";

// === Column → asset-kind mapping ===
const COLUMNS = [
  { id: "start",  label: "START",  blurb: "Intros · DJ Drops · Voice Opens · Uploads",
    kinds: ["voice", "opener"], accent: "emerald",
    aiKind: "voice",
    aiOptions: [
      { id: "podcast_intro", label: "Podcast Intro" },
      { id: "show_opener",   label: "Show Opener" },
      { id: "dj_drop",       label: "DJ Drop" },
      { id: "jingle",        label: "Jingle" },
      { id: "weather_intro", label: "Weather Intro" },
      { id: "news_intro",    label: "News Intro" },
    ] },
  { id: "middle", label: "MIDDLE", blurb: "Sweepers · Music Beds · FX · Promos · Liners",
    kinds: ["bed", "music", "fx"], accent: "amber",
    aiKind: "mixed",  // shows bed + fx + sweeper
    aiOptions: [
      { id: "voice:sweeper",  label: "🎙️ Sweeper voice" },
      { id: "voice:promo",    label: "🎙️ Promo voice" },
      { id: "voice:sponsor_tag", label: "🎙️ Sponsor Tag voice" },
      // Music beds — 12 genre categories (2 prompts each)
      { id: "bed:rnb_smooth",       label: "🎵 R&B — Smooth" },
      { id: "bed:rnb_late",         label: "🎵 R&B — Late night" },
      { id: "bed:urban_trap",       label: "🎵 Urban — Trap" },
      { id: "bed:urban_drill",      label: "🎵 Urban — Drill" },
      { id: "bed:pop_uplift",       label: "🎵 Pop — Uplifting" },
      { id: "bed:pop_summer",       label: "🎵 Pop — Summer" },
      { id: "bed:reggae_roots",     label: "🎵 Reggae — Roots" },
      { id: "bed:reggae_dub",       label: "🎵 Reggae — Dub" },
      { id: "bed:hiphop_boom",      label: "🎵 Hip-Hop — Boom bap" },
      { id: "bed:hiphop_lofi",      label: "🎵 Hip-Hop — Lo-fi" },
      { id: "bed:news_breaking",    label: "🎵 News — Breaking" },
      { id: "bed:news_briefing",    label: "🎵 News — Briefing" },
      { id: "bed:sports_hype",      label: "🎵 Sports — Stadium" },
      { id: "bed:sports_action",    label: "🎵 Sports — Action" },
      { id: "bed:talk_warm",        label: "🎵 Talk — Warm" },
      { id: "bed:talk_thought",     label: "🎵 Talk — Thoughtful" },
      { id: "bed:smooth_jazz_late", label: "🎵 Smooth Jazz — Late" },
      { id: "bed:smooth_jazz_cafe", label: "🎵 Smooth Jazz — Café" },
      { id: "bed:dance_house",      label: "🎵 Dance — Deep house" },
      { id: "bed:dance_techno",     label: "🎵 Dance — Techno" },
      { id: "bed:gospel_choir",     label: "🎵 Gospel — Choir" },
      { id: "bed:gospel_anthem",    label: "🎵 Gospel — Anthem" },
      { id: "bed:caribbean_soca",   label: "🎵 Caribbean — Soca" },
      { id: "bed:caribbean_calypso",label: "🎵 Caribbean — Calypso" },
      { id: "fx:whoosh",      label: "💥 Whoosh" },
      { id: "fx:ding",        label: "💥 Ding" },
      { id: "fx:riser",       label: "💥 Riser" },
      { id: "fx:drop",        label: "💥 Sub drop" },
      { id: "fx:impact",      label: "💥 Impact" },
      { id: "fx:swirl",       label: "💥 Reverse swirl" },
      { id: "fx:glitch",      label: "💥 Glitch buzz" },
    ] },
  { id: "end",    label: "END",    blurb: "Station IDs · Slogans · Frequency · Closers",
    kinds: ["closer", "voice"], accent: "violet",
    aiKind: "voice",
    aiOptions: [
      { id: "station_id",  label: "Station ID" },
      { id: "top_of_hour", label: "Top of Hour" },
      { id: "sponsor_tag", label: "Sponsor Tag" },
    ] },
];

const ACCENT_BG = {
  emerald: "border-emerald-500/30",
  amber:   "border-brand-amber/40",
  violet:  "border-violet-500/30",
};
const ACCENT_TXT = {
  emerald: "text-emerald-400",
  amber:   "text-brand-amber",
  violet:  "text-violet-400",
};
const TRACK_CLIP_BG = ["bg-emerald-500/30 border-emerald-400/60", "bg-brand-amber/30 border-brand-amber/60",
                       "bg-violet-500/30 border-violet-400/60", "bg-sky-500/30 border-sky-400/60"];

const PX_PER_SEC = 60;             // 60px == 1 second
const TRACK_COUNT = 4;
const TRACK_HEIGHT = 72;

function msToPx(ms) { return (ms / 1000) * PX_PER_SEC; }
function pxToMs(px) { return Math.max(0, Math.round((px / PX_PER_SEC) * 1000)); }

function fmtMs(ms) {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// ---- Asset row in a column ----
function AssetRow({ asset, onAdd, onPreview, playingId }) {
  const dur = asset.duration_ms || 0;
  return (
    <div className="group flex items-center gap-2 rounded-md border border-line bg-bg-elevated px-2.5 py-2 hover:border-brand-amber/40 transition cursor-grab active:cursor-grabbing"
         draggable
         onDragStart={(e) => {
           e.dataTransfer.setData("text/asset-id", asset.asset_id);
           e.dataTransfer.effectAllowed = "copy";
         }}
         title="Drag onto the timeline, or click + to append"
         data-testid={`seq-asset-${asset.asset_id}`}>
      <button onClick={() => onPreview(asset)}
              className="grid h-6 w-6 place-items-center rounded bg-bg-surface text-ink-secondary hover:text-cyan-300 transition"
              data-testid={`seq-preview-${asset.asset_id}`}
              aria-label="Preview">
        {playingId === asset.asset_id
          ? <Pause className="h-3 w-3" />
          : <Play className="h-3 w-3" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-ink-primary truncate" title={asset.label}>{asset.label}</div>
        <div className="font-mono text-[9px] text-ink-muted uppercase tracking-wider truncate">
          {asset.kind}{asset.genre ? ` · ${asset.genre}` : ""}{dur ? ` · ${fmtMs(dur)}` : ""}
        </div>
      </div>
      <button onClick={() => onAdd(asset)}
              className="grid h-6 w-6 place-items-center rounded bg-brand-amber/10 text-brand-amber hover:bg-brand-amber/20 transition opacity-0 group-hover:opacity-100"
              data-testid={`seq-add-${asset.asset_id}`}
              aria-label="Add to timeline">
        <Plus className="h-3 w-3" />
      </button>
    </div>
  );
}

// ---- Single column ----
function LibraryColumn({ col, assets, onAdd, onPreview, playingId, onUpload, uploadKind, onAiGenerate, aiBusy }) {
  const [q, setQ] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const filtered = useMemo(() => {
    const items = assets.filter((a) => col.kinds.includes(a.kind));
    if (!q.trim()) return items;
    const s = q.trim().toLowerCase();
    return items.filter((a) =>
      (a.label || "").toLowerCase().includes(s) ||
      (a.genre || "").toLowerCase().includes(s)
    );
  }, [assets, col.kinds, q]);

  const inputRef = useRef(null);
  const aiBoxRef = useRef(null);
  useEffect(() => {
    if (!aiOpen) return;
    const h = (e) => { if (aiBoxRef.current && !aiBoxRef.current.contains(e.target)) setAiOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [aiOpen]);

  return (
    <div className={`rounded-xl border ${ACCENT_BG[col.accent]} bg-bg-surface flex flex-col h-[420px]`}
         data-testid={`seq-col-${col.id}`}>
      <div className="px-4 py-3 border-b border-line">
        <div className="flex items-center justify-between">
          <div className={`font-mono text-[11px] uppercase tracking-[0.25em] font-bold ${ACCENT_TXT[col.accent]}`}>
            // {col.label}
          </div>
          <div className="text-[10px] text-ink-muted font-mono">{filtered.length}</div>
        </div>
        <div className="text-[10px] text-ink-muted mt-0.5">{col.blurb}</div>
      </div>
      <div className="px-3 py-2 border-b border-line flex items-center gap-2">
        <Search className="h-3 w-3 text-ink-muted shrink-0" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
               className="h-7 text-xs bg-bg border-line"
               data-testid={`seq-search-${col.id}`} />
        {uploadKind && (
          <>
            <input ref={inputRef} type="file" accept="audio/*" className="hidden"
                   onChange={(e) => onUpload(uploadKind, e.target.files?.[0])}
                   data-testid={`seq-upload-input-${col.id}`} />
            <button onClick={() => inputRef.current?.click()}
                    className="grid h-7 w-7 shrink-0 place-items-center rounded bg-bg-elevated border border-line text-ink-secondary hover:text-cyan-300 hover:border-brand-amber/40 transition"
                    data-testid={`seq-upload-btn-${col.id}`}
                    title={`Upload ${uploadKind}`}
                    aria-label="Upload">
              <Upload className="h-3 w-3" />
            </button>
          </>
        )}
        {onAiGenerate && col.aiOptions && (
          <div className="relative shrink-0" ref={aiBoxRef}>
            <button
              onClick={() => setAiOpen((o) => !o)}
              disabled={aiBusy}
              data-testid={`seq-ai-btn-${col.id}`}
              title="AI generate"
              className="grid h-7 w-7 place-items-center rounded border border-brand-amber/40 bg-brand-amber/10 text-brand-amber hover:bg-brand-amber/20 disabled:opacity-50 transition"
              aria-label="AI generate"
            >
              {aiBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
            </button>
            {aiOpen && (
              <div
                className="absolute right-0 top-9 z-30 w-56 rounded-lg border border-line bg-bg-surface shadow-2xl overflow-hidden"
                data-testid={`seq-ai-menu-${col.id}`}
              >
                <div className="px-3 py-2 border-b border-line text-[10px] font-mono uppercase tracking-wider text-brand-amber/80">
                  AI generate · {col.label}
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {col.aiOptions.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => { setAiOpen(false); onAiGenerate(col, opt.id); }}
                      data-testid={`seq-ai-opt-${col.id}-${opt.id.replace(":", "-")}`}
                      className="block w-full text-left px-3 py-2 text-xs text-ink-secondary hover:bg-bg-elevated hover:text-cyan-300 transition border-b border-line/40 last:border-0"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
        {filtered.length === 0 ? (
          <div className="text-center py-8 text-[10px] font-mono uppercase tracking-wider text-ink-muted">
            No assets · upload or hit <span className="text-brand-amber">✨ AI</span>
          </div>
        ) : filtered.map((a) => (
          <AssetRow key={a.asset_id} asset={a} onAdd={onAdd} onPreview={onPreview} playingId={playingId} />
        ))}
      </div>
    </div>
  );
}

// ---- Per-clip waveform canvas ----
function ClipWaveform({ buffer, width, height, color, trimStartMs = 0, trimEndMs = 0 }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs || !buffer || width <= 0 || height <= 0) return;
    const dpr = window.devicePixelRatio || 1;
    cvs.width = Math.floor(width * dpr);
    cvs.height = Math.floor(height * dpr);
    const ctx = cvs.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);
    const channels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    // Apply trim offsets so the waveform reflects what will actually play
    const startSample = Math.max(0, Math.floor((trimStartMs / 1000) * sampleRate));
    const endSample = Math.min(buffer.length, buffer.length - Math.floor((trimEndMs / 1000) * sampleRate));
    const usable = Math.max(1, endSample - startSample);
    const buckets = Math.max(1, Math.floor(width));
    const step = Math.max(1, Math.floor(usable / buckets));
    ctx.fillStyle = color;
    const mid = height / 2;
    for (let x = 0; x < buckets; x++) {
      let mx = 0;
      const sStart = startSample + x * step;
      const sEnd = Math.min(endSample, sStart + step);
      for (let s = sStart; s < sEnd; s += 4) {
        for (let ch = 0; ch < channels; ch++) {
          const v = Math.abs(buffer.getChannelData(ch)[s] || 0);
          if (v > mx) mx = v;
        }
      }
      const h = Math.max(1, mx * height * 0.9);
      ctx.fillRect(x, mid - h / 2, 1, h);
    }
  }, [buffer, width, height, color, trimStartMs, trimEndMs]);
  return <canvas ref={canvasRef} style={{ width, height }} className="pointer-events-none" />;
}

// ---- Clip on the timeline ----
function TimelineClip({ clip, asset, idx, selected, onSelect, onMove, onDelete, onTrim, buffer, snapMs, edges }) {
  const startPx = msToPx(clip.start_ms);
  // Real decoded audio length is the source of truth; metadata is the fallback.
  const fullDur = (buffer ? buffer.duration * 1000 : 0) || asset?.duration_ms || 4000;
  const ts = clip.trim_start_ms || 0;
  const te = clip.trim_end_ms || 0;
  const effDur = Math.max(50, fullDur - ts - te);
  const widthPx = Math.max(40, msToPx(effDur));
  const drag = useRef(null);

  const applySnap = (ms) => (snapMs > 0 ? Math.round(ms / snapMs) * snapMs : ms);

  const onMouseDownBody = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect(clip.clip_id);
    drag.current = { kind: "move", startX: e.clientX, startY: e.clientY,
                     baseMs: clip.start_ms, baseTrack: clip.track };
    const move = (ev) => {
      const dx = ev.clientX - drag.current.startX;
      const dTrack = Math.round((ev.clientY - drag.current.startY) / TRACK_HEIGHT);
      // signed px→ms (pxToMs clamps at 0, which would block dragging left)
      let ms = Math.max(0, applySnap(drag.current.baseMs + (dx / PX_PER_SEC) * 1000));
      // Magnetic snap to neighbouring clip edges (within ~10 px)
      const th = pxToMs(10);
      let best = null;
      (edges || []).forEach((ed) => {
        if (ed.clip_id === clip.clip_id) return;
        [ed.start, ed.end].forEach((x) => {
          if (Math.abs(ms - x) < th && (best === null || Math.abs(ms - x) < Math.abs(ms - best))) best = x;
          const tail = x - effDur;
          if (tail >= 0 && Math.abs(ms - tail) < th && (best === null || Math.abs(ms - tail) < Math.abs(ms - best))) best = tail;
        });
      });
      if (best !== null) ms = best;
      onMove(clip.clip_id, ms, drag.current.baseTrack + dTrack);
    };
    const up = () => {
      drag.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const onMouseDownHandle = (side) => (e) => {
    e.stopPropagation();
    onSelect(clip.clip_id);
    drag.current = { kind: side, startX: e.clientX,
                     baseTrim: side === "left" ? ts : te,
                     baseStart: clip.start_ms };
    const move = (ev) => {
      // signed px→ms (pxToMs clamps at 0)
      const dxMs = Math.round(((ev.clientX - drag.current.startX) / PX_PER_SEC) * 1000);
      if (side === "left") {
        let newTrim = applySnap(Math.max(0, drag.current.baseTrim + dxMs));
        // Don't let visible width shrink below 100 ms
        const maxTrim = fullDur - te - 100;
        if (newTrim > maxTrim) newTrim = maxTrim;
        const delta = newTrim - drag.current.baseTrim;
        const newStart = Math.max(0, drag.current.baseStart + delta);
        onTrim(clip.clip_id, "left", newTrim, newStart);
      } else {
        let newTrim = applySnap(Math.max(0, drag.current.baseTrim - dxMs));
        const maxTrim = fullDur - ts - 100;
        if (newTrim > maxTrim) newTrim = maxTrim;
        onTrim(clip.clip_id, "right", newTrim, 0);
      }
    };
    const up = () => {
      drag.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const waveColor = ["rgba(167,243,208,0.85)", "rgba(252,211,77,0.85)",
                     "rgba(196,181,253,0.85)", "rgba(125,211,252,0.85)"][clip.track % 4];

  return (
    <div
      style={{ left: startPx, width: widthPx, top: 4, height: TRACK_HEIGHT - 8 }}
      className={`absolute rounded-md border-2 ${TRACK_CLIP_BG[clip.track % TRACK_CLIP_BG.length]} cursor-grab active:cursor-grabbing select-none overflow-hidden ${selected ? "ring-2 ring-brand-amber" : ""}`}
      onMouseDown={onMouseDownBody}
      data-testid={`seq-clip-${idx}`}
    >
      {buffer && (
        <div className="absolute inset-0 pointer-events-none" data-testid={`seq-clip-wave-${idx}`}>
          <ClipWaveform buffer={buffer} width={widthPx - 4} height={TRACK_HEIGHT - 12} color={waveColor} trimStartMs={ts} trimEndMs={te} />
        </div>
      )}
      <div className="relative px-2 py-1.5 h-full overflow-hidden pointer-events-none">
        <div className="text-[10px] font-bold text-white truncate drop-shadow">{asset?.label || clip.asset_id}</div>
        <div className="font-mono text-[9px] text-white/80 uppercase tracking-wider drop-shadow">
          {fmtMs(effDur)}{(ts || te) ? ` · trim ${ts}/${te}` : ""} · {clip.volume_db > 0 ? `+${clip.volume_db}` : clip.volume_db}dB
        </div>
        {(clip.fade_in_ms > 0 || clip.fade_out_ms > 0) && (
          <div className="font-mono text-[9px] text-white/70 drop-shadow">
            {clip.fade_in_ms > 0 && `↗${clip.fade_in_ms}`}{clip.fade_in_ms > 0 && clip.fade_out_ms > 0 && " · "}{clip.fade_out_ms > 0 && `↘${clip.fade_out_ms}`}
          </div>
        )}
      </div>
      {/* Left trim handle */}
      <div
        onMouseDown={onMouseDownHandle("left")}
        className="absolute left-0 top-0 h-full w-2 cursor-ew-resize bg-white/0 hover:bg-white/30 transition"
        data-testid={`seq-clip-trim-left-${idx}`}
        title="Trim head"
      />
      {/* Right trim handle */}
      <div
        onMouseDown={onMouseDownHandle("right")}
        className="absolute right-0 top-0 h-full w-2 cursor-ew-resize bg-white/0 hover:bg-white/30 transition"
        data-testid={`seq-clip-trim-right-${idx}`}
        title="Trim tail"
      />
      <button onClick={(e) => { e.stopPropagation(); onDelete(clip.clip_id); }}
              className="absolute top-0.5 right-0.5 grid h-4 w-4 place-items-center rounded bg-black/40 text-white/80 hover:bg-rose-500 hover:text-white transition"
              aria-label="Delete clip"
              data-testid={`seq-clip-delete-${idx}`}>
        <X className="h-2.5 w-2.5" />
      </button>
    </div>
  );
}

// ---- Main Sequencer ----
export default function SequencerTab() {
  const [assets, setAssets] = useState([]);
  const [clips, setClips] = useState([]);
  const [projectName, setProjectName] = useState("Untitled project");
  const [projectId, setProjectId] = useState(null);
  const [selectedClipId, setSelectedClipId] = useState(null);
  const [dragOverTrack, setDragOverTrack] = useState(null);
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [exportFmt, setExportFmt] = useState("mp3");
  const audioRef = useRef(null);
  // Web Audio preview engine
  const [preview, setPreview] = useState({ state: "idle", progressMs: 0 });
  const previewRef = useRef({ ctx: null, sources: [], startedAt: 0, raf: 0, bufferCache: {}, startedFromMs: 0 });
  // Playhead + loop region (driven by ruler interactions)
  const [playheadMs, setPlayheadMs] = useState(0);
  const [loopRegion, setLoopRegion] = useState(null);  // { start_ms, end_ms } | null
  const rulerDrag = useRef(null);
  // Decoded audio buffers (shared with preview engine) — for waveform rendering
  const [audioBuffers, setAudioBuffers] = useState({});
  const decodeCtxRef = useRef(null);

  // Load assets + projects on mount
  const loadAssets = async () => {
    try {
      const res = await api.get("/imaging/assets");
      setAssets(res.data || []);
    } catch { /* silent */ }
  };
  const loadProjects = async () => {
    try {
      const res = await api.get("/imaging/projects");
      setProjects(res.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadAssets(); loadProjects(); }, []);

  const assetById = useMemo(() => {
    const m = {}; assets.forEach((a) => { m[a.asset_id] = a; }); return m;
  }, [assets]);

  // === Asset actions ===
  const previewAsset = (asset) => {
    if (playingId === asset.asset_id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    const src = asset.audio_url || `/api/imaging/assets/${asset.asset_id}/audio`;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = src;
      audioRef.current.play().catch(() => {});
    }
    setPlayingId(asset.asset_id);
  };

  const aiGenerateAsset = async (col, optId) => {
    // optId is either "<subtype>" (for voice/end columns) or "<kind>:<subtype>" (mixed column)
    let kind = col.aiKind === "mixed" ? null : col.aiKind;
    let subtype = optId;
    if (optId.includes(":")) {
      const [k, s] = optId.split(":");
      kind = k; subtype = s;
    }
    setBusy(`ai-${col.id}`);
    try {
      const { data } = await api.post("/imaging/ai-generate", { kind, subtype });
      toast.success(`Generated · ${data.label}`);
      await loadAssets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generation failed");
    } finally {
      setBusy(null);
    }
  };

  const [packBusy, setPackBusy] = useState(false);
  const loadStarterPack = async () => {
    if (!await appConfirm("Generate 6 voice pieces + 8 stingers + 24 music beds (12 categories) + 7 FX? This takes 60-120s.")) return;
    setPackBusy(true);
    try {
      const { data } = await api.post("/imaging/starter-pack");
      toast.success(`Studio loaded · ${data.counts.voice} voices · ${data.counts.stinger} stingers · ${data.counts.bed} beds · ${data.counts.fx} FX`);
      await loadAssets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Starter pack failed");
    } finally {
      setPackBusy(false);
    }
  };


  const upload = async (kind, file) => {
    if (!file) return;
    setBusy("upload");
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", kind);
      form.append("label", file.name);
      await api.post("/imaging/assets/upload", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind} uploaded`);
      await loadAssets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(null); }
  };

  // === Clip operations ===
  const nextTrackForAsset = (asset) => {
    // Round-robin: voice/opener → 0, bed/music → 1, fx → 2, closer → 3
    if (asset.kind === "voice" || asset.kind === "opener") return 0;
    if (asset.kind === "bed" || asset.kind === "music") return 1;
    if (asset.kind === "fx") return 2;
    if (asset.kind === "closer") return 3;
    return 0;
  };

  const addClip = (asset) => {
    const track = nextTrackForAsset(asset);
    // Find the rightmost clip on this track to place new clip after it.
    const sameTrack = clips.filter((c) => c.track === track);
    const lastEnd = sameTrack.reduce((max, c) => {
      const a = assetById[c.asset_id];
      const buf = audioBuffers[c.asset_id];
      const end = c.start_ms + ((buf ? buf.duration * 1000 : 0) || a?.duration_ms || 0);
      return Math.max(max, end);
    }, 0);
    const newClip = {
      clip_id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      asset_id: asset.asset_id, track, start_ms: lastEnd,
      volume_db: 0, fade_in_ms: 0, fade_out_ms: 0,
      trim_start_ms: 0, trim_end_ms: 0,
    };
    setClips((cs) => [...cs, newClip]);
    setSelectedClipId(newClip.clip_id);
  };

  const addClipAt = (assetId, track, startMs) => {
    const newClip = {
      clip_id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      asset_id: assetId,
      track: Math.max(0, Math.min(TRACK_COUNT - 1, track)),
      start_ms: Math.max(0, snap(startMs)),
      volume_db: 0, fade_in_ms: 0, fade_out_ms: 0,
      trim_start_ms: 0, trim_end_ms: 0,
    };
    setClips((cs) => [...cs, newClip]);
    setSelectedClipId(newClip.clip_id);
  };

  const moveClip = (cid, newStartMs, newTrack) =>
    setClips((cs) => cs.map((c) => c.clip_id === cid
      ? { ...c, start_ms: Math.max(0, newStartMs),
          ...(newTrack != null ? { track: Math.max(0, Math.min(TRACK_COUNT - 1, newTrack)) } : {}) }
      : c));
  const trimClip = (cid, side, newTrimMs, newStartMs) =>
    setClips((cs) => cs.map((c) => {
      if (c.clip_id !== cid) return c;
      if (side === "left") {
        return { ...c, trim_start_ms: Math.max(0, newTrimMs), start_ms: Math.max(0, newStartMs) };
      }
      return { ...c, trim_end_ms: Math.max(0, newTrimMs) };
    }));
  const deleteClip = (cid) =>
    setClips((cs) => cs.filter((c) => c.clip_id !== cid));
  const updateClip = (cid, patch) =>
    setClips((cs) => cs.map((c) => c.clip_id === cid ? { ...c, ...patch } : c));

  const totalMs = useMemo(() => clips.reduce((m, c) => {
    const a = assetById[c.asset_id];
    const buf = audioBuffers[c.asset_id];
    const full = (buf ? buf.duration * 1000 : 0) || a?.duration_ms || 0;
    const eff = Math.max(50, full - (c.trim_start_ms || 0) - (c.trim_end_ms || 0));
    return Math.max(m, c.start_ms + eff);
  }, 5000), [clips, assetById, audioBuffers]);

  // Edges of every clip — used for magnetic snap while dragging
  const clipEdges = useMemo(() => clips.map((c) => {
    const a = assetById[c.asset_id];
    const buf = audioBuffers[c.asset_id];
    const full = (buf ? buf.duration * 1000 : 0) || a?.duration_ms || 0;
    const eff = Math.max(50, full - (c.trim_start_ms || 0) - (c.trim_end_ms || 0));
    return { clip_id: c.clip_id, start: c.start_ms, end: c.start_ms + eff };
  }), [clips, assetById, audioBuffers]);
  const totalPx = msToPx(totalMs + 2000);

  const selectedClip = clips.find((c) => c.clip_id === selectedClipId);

  // === Project actions ===
  const saveProject = async () => {
    setBusy("save");
    try {
      const payload = { name: projectName, clips, duration_ms: totalMs, notes: "" };
      if (projectId) {
        await api.patch(`/imaging/projects/${projectId}`, payload);
        toast.success(`"${projectName}" saved · find it in the “Open project…” dropdown`);
      } else {
        const res = await api.post("/imaging/projects", payload);
        setProjectId(res.data.project_id);
        toast.success(`"${projectName}" created · listed under “Open project…” above. Click Export to render an audio file for the Library.`, { duration: 6000 });
      }
      await loadProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(null); }
  };

  const loadProject = async (pid) => {
    if (!pid || pid === "__new__") {
      setProjectId(null);
      setProjectName("Untitled project");
      setClips([]);
      setSelectedClipId(null);
      return;
    }
    setBusy("load");
    try {
      const res = await api.get(`/imaging/projects/${pid}`);
      setProjectId(res.data.project_id);
      setProjectName(res.data.name);
      setClips(res.data.clips || []);
      setSelectedClipId(null);
    } catch (e) {
      toast.error("Failed to load project");
    } finally { setBusy(null); }
  };

  const exportProject = async () => {
    if (!projectId) {
      toast.error("Save the project first");
      return;
    }
    if (clips.length === 0) {
      toast.error("Add some clips first");
      return;
    }
    setBusy("export");
    try {
      const res = await api.post(`/imaging/projects/${projectId}/export?fmt=${exportFmt}`);
      toast.success(`Exported · cart ${res.data.piece.cart_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally { setBusy(null); }
  };

  const clearProject = async () => {
    if (clips.length > 0 && !(await appConfirm("Clear the timeline?"))) return;
    setClips([]);
    setSelectedClipId(null);
  };

  const autoMix = async () => {
    if (!projectId) { toast.error("Save the project first"); return; }
    if (clips.length === 0) { toast.error("Add some clips first"); return; }
    if (!await appConfirm("Auto-Mix will re-align clips, add crossfades, duck beds under voice, and adjust levels. Continue?")) return;
    setBusy("automix");
    try {
      // Persist current state first (in case the user hasn't saved)
      await api.patch(`/imaging/projects/${projectId}`, { name: projectName, clips, duration_ms: totalMs, notes: "" });
      const res = await api.post(`/imaging/projects/${projectId}/automix`);
      setClips(res.data.clips || []);
      toast.success("Auto-mixed · clips re-aligned, ducked & cross-faded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-mix failed");
    } finally { setBusy(null); }
  };

  // === Web Audio preview ===
  const stopPreview = useCallback(() => {
    const p = previewRef.current;
    p.sources.forEach((s) => { try { s.stop(); } catch (_) { /* ignore */ } });
    p.sources = [];
    if (p.raf) { cancelAnimationFrame(p.raf); p.raf = 0; }
    if (p.ctx && p.ctx.state !== "closed") { p.ctx.close().catch(() => {}); }
    p.ctx = null;
    setPreview({ state: "idle", progressMs: 0 });
  }, []);

  const startPreview = useCallback(async () => {
    if (clips.length === 0) { toast.error("Add some clips first"); return; }
    setPreview({ state: "loading", progressMs: 0 });
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) { toast.error("Web Audio not supported in this browser"); setPreview({ state: "idle", progressMs: 0 }); return; }
    const ctx = new Ctx();
    const p = previewRef.current;
    p.ctx = ctx; p.sources = [];
    const dbToGain = (db) => Math.pow(10, (db || 0) / 20);

    // Loop mode? otherwise scrub-from playhead
    const looping = !!(loopRegion && loopRegion.end_ms > loopRegion.start_ms);
    const startMs = looping ? loopRegion.start_ms : playheadMs;
    const loopLenMs = looping ? loopRegion.end_ms - loopRegion.start_ms : 0;
    const iterations = looping ? 16 : 1;  // up to 16 loop passes, user can stop

    try {
      const uniqIds = Array.from(new Set(clips.map((c) => c.asset_id)));
      const buffers = { ...p.bufferCache };
      await Promise.all(uniqIds.map(async (aid) => {
        if (buffers[aid]) return;
        const res = await fetch(`${API_BASE}/imaging/assets/${aid}/audio`, { credentials: "include" });
        if (!res.ok) throw new Error(`asset ${aid.slice(0, 6)} fetch ${res.status}`);
        const arr = await res.arrayBuffer();
        buffers[aid] = await ctx.decodeAudioData(arr);
      }));
      p.bufferCache = buffers;

      const t0 = ctx.currentTime + 0.05;
      let furthestMs = 0;
      for (let iter = 0; iter < iterations; iter++) {
        const iterT0 = t0 + (iter * loopLenMs) / 1000;
        clips.forEach((c) => {
          const buf = buffers[c.asset_id];
          if (!buf) return;
          const tsMs = c.trim_start_ms || 0;
          const teMs = c.trim_end_ms || 0;
          const effDur = Math.max(0.05, buf.duration - tsMs / 1000 - teMs / 1000);
          const clipStartMs = c.start_ms || 0;
          const clipEndMs = clipStartMs + effDur * 1000;

          // Visible window in absolute timeline ms
          const winStart = looping ? loopRegion.start_ms : startMs;
          const winEnd = looping ? loopRegion.end_ms : Number.POSITIVE_INFINITY;

          // Intersect clip with window
          const overlapStart = Math.max(clipStartMs, winStart);
          const overlapEnd = Math.min(clipEndMs, winEnd);
          if (overlapEnd <= overlapStart) return;

          // Map overlap into source-buffer time (account for trim_start)
          const offsetIntoBuffer = tsMs / 1000 + (overlapStart - clipStartMs) / 1000;
          const playDur = (overlapEnd - overlapStart) / 1000;
          const whenSec = iterT0 + (overlapStart - winStart) / 1000;

          const src = ctx.createBufferSource();
          src.buffer = buf;
          const g = ctx.createGain();
          const target = dbToGain(c.volume_db || 0);
          // Fades anchored to the effective (trimmed) clip
          const clipFiSec = Math.min((c.fade_in_ms || 0) / 1000, effDur);
          const clipFoSec = Math.min((c.fade_out_ms || 0) / 1000, effDur);
          const clipFiEndAbs = clipStartMs / 1000 + clipFiSec;
          const clipFoStartAbs = clipEndMs / 1000 - clipFoSec;
          const overlapStartAbs = overlapStart / 1000;
          const overlapEndAbs = overlapEnd / 1000;

          const computeAt = (absSec) => {
            if (clipFiSec > 0 && absSec < clipFiEndAbs) {
              const t = Math.max(0, (absSec - clipStartMs / 1000) / clipFiSec);
              return Math.max(0, Math.min(1, t)) * target;
            }
            if (clipFoSec > 0 && absSec > clipFoStartAbs) {
              const t = Math.max(0, 1 - (absSec - clipFoStartAbs) / clipFoSec);
              return Math.max(0, Math.min(1, t)) * target;
            }
            return target;
          };

          const startGain = computeAt(overlapStartAbs);
          const endGain = computeAt(overlapEndAbs);
          g.gain.setValueAtTime(startGain, whenSec);
          if (clipFiSec > 0 && clipFiEndAbs > overlapStartAbs && clipFiEndAbs < overlapEndAbs) {
            const at = whenSec + (clipFiEndAbs - overlapStartAbs);
            g.gain.linearRampToValueAtTime(target, at);
          }
          if (clipFoSec > 0 && clipFoStartAbs > overlapStartAbs && clipFoStartAbs < overlapEndAbs) {
            const at = whenSec + (clipFoStartAbs - overlapStartAbs);
            g.gain.linearRampToValueAtTime(target, at);
          }
          g.gain.linearRampToValueAtTime(endGain, whenSec + playDur);

          src.connect(g).connect(ctx.destination);
          src.start(whenSec, offsetIntoBuffer, playDur);
          p.sources.push(src);
          furthestMs = Math.max(furthestMs, (whenSec - t0) * 1000 + playDur * 1000);
        });
      }

      p.startedAt = performance.now();
      p.startedFromMs = startMs;
      setPreview({ state: "playing", progressMs: startMs });
      const totalRunMs = Math.max(furthestMs, 500);
      const tick = () => {
        const elapsed = performance.now() - p.startedAt;
        if (elapsed >= totalRunMs + 100) {
          stopPreview();
          return;
        }
        let pos;
        if (looping) {
          pos = loopRegion.start_ms + (elapsed % loopLenMs);
        } else {
          pos = startMs + elapsed;
        }
        setPreview({ state: "playing", progressMs: pos });
        p.raf = requestAnimationFrame(tick);
      };
      p.raf = requestAnimationFrame(tick);
    } catch (e) {
      toast.error(`Preview failed: ${e.message || "unknown"}`);
      stopPreview();
    }
  }, [clips, stopPreview, playheadMs, loopRegion]);

  // Snap-to-grid for drag + trim operations (ms; 0 = off)
  const [snapMs, setSnapMs] = useState(50);
  const snap = useCallback((ms) => (snapMs > 0 ? Math.round(ms / snapMs) * snapMs : ms), [snapMs]);

  // AI suggestions at the playhead
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsBusy, setSuggestionsBusy] = useState(false);

  // Compute context for suggestion fetch
  const playheadContext = useMemo(() => {
    if (playheadMs <= 0) return null;
    let left = null, right = null, gap = null;
    let bestLeftEnd = -Infinity, bestRightStart = Infinity;
    clips.forEach((c) => {
      const a = assetById[c.asset_id];
      const buf = audioBuffers[c.asset_id];
      const full = (buf ? buf.duration * 1000 : 0) || a?.duration_ms || 0;
      const eff = Math.max(50, full - (c.trim_start_ms || 0) - (c.trim_end_ms || 0));
      const endMs = c.start_ms + eff;
      if (endMs <= playheadMs && endMs > bestLeftEnd) { bestLeftEnd = endMs; left = a?.kind || null; }
      if (c.start_ms >= playheadMs && c.start_ms < bestRightStart) { bestRightStart = c.start_ms; right = a?.kind || null; }
    });
    if (bestLeftEnd > -Infinity && bestRightStart < Infinity) {
      gap = Math.max(0, bestRightStart - Math.max(bestLeftEnd, playheadMs));
    }
    return { playhead_ms: playheadMs, nearest_left_kind: left, nearest_right_kind: right, gap_ms: gap,
             used_asset_ids: Array.from(new Set(clips.map((c) => c.asset_id))) };
  }, [playheadMs, clips, assetById, audioBuffers]);

  useEffect(() => {
    if (!playheadContext) { setSuggestions([]); return; }
    const handle = setTimeout(() => {
      setSuggestionsBusy(true);
      api.post("/imaging/sequencer/suggest", playheadContext)
        .then((r) => setSuggestions(r.data.suggestions || []))
        .catch(() => setSuggestions([]))
        .finally(() => setSuggestionsBusy(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [playheadContext]);

  const dropSuggestion = (s) => {
    const asset = assets.find((a) => a.asset_id === s.asset_id);
    if (!asset) return;
    const track = nextTrackForAsset(asset);
    const newClip = {
      clip_id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      asset_id: asset.asset_id, track, start_ms: snap(playheadMs),
      volume_db: 0, fade_in_ms: 0, fade_out_ms: 0,
      trim_start_ms: 0, trim_end_ms: 0,
    };
    setClips((cs) => [...cs, newClip]);
    setSelectedClipId(newClip.clip_id);
    toast.success(`Dropped at ${fmtMs(playheadMs)}`);
  };

  // Stop preview on unmount or when clips meaningfully change
  useEffect(() => () => {
    stopPreview();
    if (decodeCtxRef.current && decodeCtxRef.current.state !== "closed") {
      decodeCtxRef.current.close().catch(() => {});
    }
    decodeCtxRef.current = null;
  }, [stopPreview]);

  // Background: fetch + decode AudioBuffers for any asset on the timeline so we can
  // render waveforms. Reuses the same context across clips.
  useEffect(() => {
    if (clips.length === 0) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!decodeCtxRef.current) decodeCtxRef.current = new Ctx();
    const ctx = decodeCtxRef.current;
    const missing = Array.from(new Set(clips.map((c) => c.asset_id))).filter((id) => !audioBuffers[id]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const aid of missing) {
        try {
          const res = await fetch(`${API_BASE}/imaging/assets/${aid}/audio`, { credentials: "include" });
          if (!res.ok) continue;
          const arr = await res.arrayBuffer();
          const buf = await ctx.decodeAudioData(arr);
          if (cancelled) return;
          setAudioBuffers((prev) => ({ ...prev, [aid]: buf }));
          // Also seed the preview engine's cache so first preview is instant
          previewRef.current.bufferCache[aid] = buf;
        } catch (_) { /* ignore individual failures */ }
      }
    })();
    return () => { cancelled = true; };
  }, [clips, audioBuffers]);

  // Per-column upload kind: START → voice, MIDDLE → music, END → closer
  const colUploadKind = (id) => ({ start: "voice", middle: "music", end: "closer" }[id]);

  return (
    <div className="space-y-4" data-testid="seq-root">
      <audio ref={audioRef} onEnded={() => setPlayingId(null)} className="hidden" />

      {/* Project bar */}
      <div className="rounded-xl border border-line bg-bg-surface p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="seq-toolbar">
        <Select value={projectId || "__new__"} onValueChange={loadProject}>
          <SelectTrigger className="md:w-64 bg-bg-elevated border-line h-9 text-xs" data-testid="seq-project-select">
            <FolderOpen className="h-3 w-3 mr-1.5" /><SelectValue placeholder="Open saved project…" />
          </SelectTrigger>
          <SelectContent className="bg-bg-elevated border-line max-h-72">
            <SelectItem value="__new__">New project</SelectItem>
            {projects.map((p) => (
              <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input value={projectName} onChange={(e) => setProjectName(e.target.value)}
               className="md:max-w-md bg-bg-elevated border-line h-9 text-xs"
               placeholder="Project name" data-testid="seq-project-name" />
        <div className="flex-1" />
        {(playheadMs > 0 || loopRegion) && (
          <div className="flex items-center gap-2" data-testid="seq-playhead-info">
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
              {loopRegion
                ? `Loop · ${fmtMs(loopRegion.start_ms)} → ${fmtMs(loopRegion.end_ms)}`
                : `Playhead · ${fmtMs(playheadMs)}`}
            </span>
            <button
              onClick={() => { setLoopRegion(null); setPlayheadMs(0); }}
              data-testid="seq-clear-playhead"
              className="rounded p-1 text-ink-muted hover:text-rose-400 transition"
              aria-label="Reset playhead"
              title="Reset playhead + loop"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={preview.state === "playing" || preview.state === "loading" ? stopPreview : startPreview}
                  data-testid="seq-preview-btn"
                  className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs transition ${
                    preview.state === "playing"
                      ? "border-rose-500/60 bg-rose-500/20 text-rose-300 hover:bg-rose-500/30"
                      : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                  }`}>
            {preview.state === "loading" ? <Loader2 className="h-3 w-3 animate-spin" />
              : preview.state === "playing" ? <Square className="h-3 w-3 fill-current" />
              : <Play className="h-3 w-3 fill-current" />}
            {preview.state === "loading" ? "Loading…"
              : preview.state === "playing" ? "Stop"
              : "Preview mix"}
          </button>
          <button onClick={saveProject} disabled={busy === "save"}
                  data-testid="seq-save-btn"
                  title="Save the timeline as a project (find it in the “Open project…” dropdown). To get an audio file, click Export."
                  className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs hover:border-brand-amber/50 hover:text-cyan-300 transition disabled:opacity-40">
            {busy === "save" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
          </button>
          <button onClick={clearProject}
                  data-testid="seq-clear-btn"
                  className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs text-ink-secondary hover:text-rose-400 transition">
            <Trash2 className="h-3 w-3" /> Clear
          </button>
          <button onClick={autoMix} disabled={busy === "automix" || !projectId}
                  data-testid="seq-automix-btn"
                  title="Auto-align clips, crossfade boundaries, duck beds under voice, normalize levels"
                  className="inline-flex items-center gap-1.5 rounded-md border border-violet-500/50 bg-violet-500/10 px-3 py-1.5 text-xs text-violet-200 hover:bg-violet-500/20 disabled:opacity-40 transition">
            {busy === "automix" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            Auto-Mix
          </button>
          <Select value={String(snapMs)} onValueChange={(v) => setSnapMs(Number(v))}>
            <SelectTrigger className="w-24 h-7 text-xs bg-bg-elevated border-line" data-testid="seq-snap-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-bg-elevated border-line">
              <SelectItem value="0">No snap</SelectItem>
              <SelectItem value="10">10 ms</SelectItem>
              <SelectItem value="50">50 ms</SelectItem>
              <SelectItem value="100">100 ms</SelectItem>
              <SelectItem value="250">¼ s</SelectItem>
              <SelectItem value="500">½ s</SelectItem>
              <SelectItem value="1000">1 s</SelectItem>
            </SelectContent>
          </Select>
          <Select value={exportFmt} onValueChange={setExportFmt}>
            <SelectTrigger className="w-20 h-7 text-xs bg-bg-elevated border-line" data-testid="seq-export-fmt"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-bg-elevated border-line">
              <SelectItem value="mp3">MP3</SelectItem>
              <SelectItem value="wav">WAV</SelectItem>
            </SelectContent>
          </Select>
          <button onClick={exportProject} disabled={busy === "export" || !projectId}
                  data-testid="seq-export-btn"
                  title="Render the timeline to an audio file (MP3/WAV). The file appears in the Imaging → Library tab."
                  className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand-amber/90 disabled:opacity-40 transition">
            {busy === "export" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} Export
          </button>
        </div>
      </div>

      {/* 3 library columns */}
      {assets.length === 0 && (
        <div className="mb-3 rounded-lg border border-brand-amber/40 bg-gradient-to-r from-brand-amber/10 to-transparent p-4 flex items-center justify-between gap-3" data-testid="seq-starter-banner">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded-md bg-brand-amber/20 flex items-center justify-center shrink-0">
              <Package className="h-4 w-4 text-brand-amber" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-sm">Your studio is empty.</div>
              <div className="text-xs text-ink-muted">Load the demo pack — 6 voice pieces, 8 branding stingers, 24 music beds across 12 categories, and 7 broadcast FX.</div>
            </div>
          </div>
          <button
            onClick={loadStarterPack}
            disabled={packBusy}
            data-testid="seq-load-starter-pack"
            className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black amber-glow disabled:opacity-50 transition shrink-0"
          >
            {packBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
            {packBusy ? "Building pack…" : "Load starter pack"}
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="seq-columns">
        {COLUMNS.map((col) => (
          <LibraryColumn
            key={col.id}
            col={col}
            assets={assets}
            onAdd={addClip}
            onPreview={previewAsset}
            playingId={playingId}
            onUpload={upload}
            uploadKind={colUploadKind(col.id)}
            onAiGenerate={aiGenerateAsset}
            aiBusy={busy === `ai-${col.id}`}
          />
        ))}
      </div>

      {/* Timeline */}
      <div className="rounded-xl border border-line bg-bg-surface" data-testid="seq-timeline-wrap">
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">// TIMELINE</div>
          <div className="font-mono text-[10px] text-ink-muted">
            {clips.length} clips · {fmtMs(totalMs)} · 4 tracks
          </div>
        </div>
        <div className="overflow-x-auto" data-testid="seq-timeline-scroll">
          <div className="relative" style={{ width: Math.max(totalPx, 600) }}>
            {/* Ruler — click to scrub, drag to set loop region */}
            <div
              className="h-5 border-b border-line relative bg-bg-elevated cursor-crosshair select-none"
              data-testid="seq-ruler"
              onMouseDown={(e) => {
                if (preview.state === "playing" || preview.state === "loading") return;
                const rect = e.currentTarget.getBoundingClientRect();
                const startMs = pxToMs(e.clientX - rect.left);
                rulerDrag.current = { rect, startMs, lastMs: startMs };
                const move = (ev) => {
                  if (!rulerDrag.current) return;
                  rulerDrag.current.lastMs = pxToMs(ev.clientX - rulerDrag.current.rect.left);
                };
                const up = (ev) => {
                  if (!rulerDrag.current) return;
                  const endMs = pxToMs(ev.clientX - rulerDrag.current.rect.left);
                  const a = Math.min(rulerDrag.current.startMs, endMs);
                  const b = Math.max(rulerDrag.current.startMs, endMs);
                  if (b - a > 150) {
                    // Drag → loop region
                    setLoopRegion({ start_ms: a, end_ms: b });
                    setPlayheadMs(a);
                  } else {
                    // Click → playhead, clear loop
                    setLoopRegion(null);
                    setPlayheadMs(a);
                  }
                  rulerDrag.current = null;
                  window.removeEventListener("mousemove", move);
                  window.removeEventListener("mouseup", up);
                };
                window.addEventListener("mousemove", move);
                window.addEventListener("mouseup", up);
              }}
              onDoubleClick={() => { setLoopRegion(null); setPlayheadMs(0); }}
              title="Click to scrub · Drag to set loop region · Double-click to reset"
            >
              {Array.from({ length: Math.ceil((totalPx + 60) / PX_PER_SEC) }).map((_, i) => (
                <div key={i} style={{ left: i * PX_PER_SEC }} className="absolute top-0 h-full border-l border-line/60 pointer-events-none">
                  <span className="ml-1 text-[9px] font-mono text-ink-muted">{i}s</span>
                </div>
              ))}
              {/* Loop region marker on ruler */}
              {loopRegion && (
                <div
                  style={{ left: msToPx(loopRegion.start_ms), width: Math.max(2, msToPx(loopRegion.end_ms - loopRegion.start_ms)) }}
                  className="absolute top-0 h-full bg-brand-amber/40 border-x border-brand-amber pointer-events-none"
                  data-testid="seq-loop-marker"
                />
              )}
              {/* Playhead marker on ruler */}
              {(preview.state !== "playing") && (
                <div
                  style={{ left: msToPx(playheadMs) - 1 }}
                  className="absolute top-0 h-full w-px bg-brand-amber/80 pointer-events-none"
                  data-testid="seq-playhead-marker"
                />
              )}
            </div>
            {/* Loop region overlay extending across all tracks */}
            {loopRegion && (
              <div
                style={{ left: msToPx(loopRegion.start_ms),
                         width: Math.max(2, msToPx(loopRegion.end_ms - loopRegion.start_ms)),
                         height: TRACK_COUNT * TRACK_HEIGHT }}
                className="absolute top-5 bg-brand-amber/10 border-x border-brand-amber/40 pointer-events-none z-0"
                data-testid="seq-loop-overlay"
              />
            )}
            {/* Stationary playhead when stopped */}
            {preview.state === "idle" && playheadMs > 0 && (
              <div
                style={{ left: msToPx(playheadMs), height: TRACK_COUNT * TRACK_HEIGHT + 20, top: 0 }}
                className="absolute w-px bg-brand-amber/70 pointer-events-none z-10"
                data-testid="seq-playhead-cursor"
              />
            )}
            {/* Play cursor (Web Audio preview) */}
            {preview.state === "playing" && (
              <div
                style={{ left: msToPx(preview.progressMs), height: TRACK_COUNT * TRACK_HEIGHT + 20, top: 0 }}
                className="absolute w-px bg-brand-amber pointer-events-none z-10"
                data-testid="seq-play-cursor"
              >
                <div className="absolute -top-1 -left-1.5 h-2.5 w-2.5 rounded-full bg-brand-amber shadow-[0_0_8px_rgba(250,204,21,0.8)]" />
              </div>
            )}
            {/* Tracks */}
            {Array.from({ length: TRACK_COUNT }).map((_, t) => (
              <div key={t} style={{ height: TRACK_HEIGHT }}
                   className={`relative border-b border-line/40 bg-gradient-to-r from-bg-elevated/40 to-bg/60 ${dragOverTrack === t ? "bg-brand-amber/10 outline outline-1 outline-brand-amber/50" : ""}`}
                   data-testid={`seq-track-${t}`}
                   onClick={() => setSelectedClipId(null)}
                   onDragOver={(e) => {
                     e.preventDefault();
                     e.dataTransfer.dropEffect = "copy";
                     if (dragOverTrack !== t) setDragOverTrack(t);
                   }}
                   onDragLeave={() => setDragOverTrack((cur) => (cur === t ? null : cur))}
                   onDrop={(e) => {
                     e.preventDefault();
                     setDragOverTrack(null);
                     const aid = e.dataTransfer.getData("text/asset-id");
                     if (!aid) return;
                     const rect = e.currentTarget.getBoundingClientRect();
                     addClipAt(aid, t, pxToMs(e.clientX - rect.left));
                   }}>
                <div className="absolute left-2 top-1 font-mono text-[9px] uppercase tracking-wider text-ink-muted/80 pointer-events-none">
                  Track {t + 1}
                </div>
                {clips.filter((c) => c.track === t).map((c, idx) => (
                  <TimelineClip
                    key={c.clip_id}
                    clip={c}
                    asset={assetById[c.asset_id]}
                    buffer={audioBuffers[c.asset_id]}
                    snapMs={snapMs}
                    edges={clipEdges}
                    idx={`${t}-${idx}`}
                    selected={selectedClipId === c.clip_id}
                    onSelect={setSelectedClipId}
                    onMove={moveClip}
                    onTrim={trimClip}
                    onDelete={deleteClip}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI suggestions at playhead */}
      {playheadMs > 0 && preview.state !== "playing" && (
        <div className="rounded-xl border border-violet-500/40 bg-bg-surface p-4" data-testid="seq-suggestions">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-violet-300" />
              <div className="font-mono text-[10px] uppercase tracking-wider text-violet-300">
                // AI · Suggestions at {fmtMs(playheadMs)}
              </div>
            </div>
            {playheadContext?.gap_ms != null && (
              <div className="font-mono text-[10px] text-ink-muted">Gap · {fmtMs(playheadContext.gap_ms)}</div>
            )}
          </div>
          {suggestionsBusy && suggestions.length === 0 ? (
            <div className="flex items-center gap-2 text-xs text-ink-muted">
              <Loader2 className="h-3 w-3 animate-spin" /> Finding best-fit clips…
            </div>
          ) : suggestions.length === 0 ? (
            <div className="text-xs text-ink-muted">
              No suggestions yet. Add or upload a few assets first.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {suggestions.map((s, i) => (
                <button key={s.asset_id}
                        onClick={() => dropSuggestion(s)}
                        data-testid={`seq-suggestion-${i}`}
                        className="group text-left rounded-lg border border-line bg-bg-elevated hover:border-violet-400/60 hover:bg-violet-500/10 p-3 transition">
                  <div className="font-mono text-[9px] uppercase tracking-wider text-violet-300 mb-1">
                    {s.kind}{s.genre ? ` · ${s.genre}` : ""}
                  </div>
                  <div className="text-xs font-medium text-ink-primary truncate" title={s.label}>{s.label}</div>
                  <div className="font-mono text-[9px] text-ink-muted mt-1">
                    {s.duration_ms ? fmtMs(s.duration_ms) : "—"}
                  </div>
                  <div className="mt-2 text-[10px] font-mono text-violet-300 opacity-0 group-hover:opacity-100 transition">
                    Drop at playhead →
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Selected clip inspector */}
      {selectedClip && (
        <div className="rounded-xl border border-brand-amber/40 bg-bg-surface p-4" data-testid="seq-inspector">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">// CLIP INSPECTOR</div>
            <div className="text-xs text-ink-secondary truncate max-w-xs" title={assetById[selectedClip.asset_id]?.label}>
              {assetById[selectedClip.asset_id]?.label}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <ClipKnob label={`Volume (${selectedClip.volume_db > 0 ? "+" : ""}${selectedClip.volume_db} dB)`}
                      value={[selectedClip.volume_db]} min={-24} max={6} step={0.5}
                      onChange={([v]) => updateClip(selectedClip.clip_id, { volume_db: v })}
                      testid="seq-inspect-volume" />
            <ClipKnob label={`Fade in (${selectedClip.fade_in_ms} ms)`}
                      value={[selectedClip.fade_in_ms]} min={0} max={3000} step={50}
                      onChange={([v]) => updateClip(selectedClip.clip_id, { fade_in_ms: v })}
                      testid="seq-inspect-fadein" />
            <ClipKnob label={`Fade out (${selectedClip.fade_out_ms} ms)`}
                      value={[selectedClip.fade_out_ms]} min={0} max={3000} step={50}
                      onChange={([v]) => updateClip(selectedClip.clip_id, { fade_out_ms: v })}
                      testid="seq-inspect-fadeout" />
          </div>
        </div>
      )}
    </div>
  );
}

function ClipKnob({ label, value, min, max, step, onChange, testid }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      <Label className="text-[10px] uppercase tracking-wider text-ink-muted font-mono">{label}</Label>
      <Slider value={value} min={min} max={max} step={step} onValueChange={onChange} />
    </div>
  );
}
